import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, CalendarRange, Target, Gauge, Crosshair, Clock, Plus, Settings2, ClipboardList, Trophy, Layers } from "lucide-react";
import { AttendanceRegisterEditor } from "@/components/AttendanceQuickPickerDialog";
import SeasonTimeline from "@/components/SeasonTimeline";
import { toast } from "sonner";

const LOADS = ["Basso", "Medio", "Medio-Alto", "Alto", "Scarico"];
const SPECS = ["Bassa", "Media", "Alta", "Molto Alta"];
const DAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const loadTone = { Basso: "bg-emerald-50 text-emerald-700 border-emerald-200", Medio: "bg-sky-50 text-sky-700 border-sky-200", "Medio-Alto": "bg-amber-50 text-amber-700 border-amber-200", Alto: "bg-red-50 text-red-700 border-red-200", Scarico: "bg-zinc-100 text-zinc-600 border-zinc-200" };
const specTone = { Bassa: "bg-zinc-100 text-zinc-600 border-zinc-200", Media: "bg-sky-50 text-sky-700 border-sky-200", Alta: "bg-violet-50 text-violet-700 border-violet-200", "Molto Alta": "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200" };

function mondayOf(d) { const x = new Date(d); const off = (x.getDay() + 6) % 7; x.setDate(x.getDate() - off); x.setHours(0, 0, 0, 0); return x; }
function iso(d) { const x = new Date(d); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`; }
function extractTime(s) { const m = String(s || "").match(/T?(\d{2}:\d{2})/); return m ? m[1] : ""; }
function dayLabel(s) { const m = String(s || "").match(/^(\d{4})-(\d{2})-(\d{2})/); if (!m) return s; const d = new Date(+m[1], +m[2] - 1, +m[3]); return d.toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "short" }); }

export default function Programming() {
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState("");
  const [weekStart, setWeekStart] = useState(iso(mondayOf(new Date())));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mcOpen, setMcOpen] = useState(false);
  const [profOpen, setProfOpen] = useState(false);
  const [sessOpen, setSessOpen] = useState(false);
  const [registerId, setRegisterId] = useState(null);
  const [comps, setComps] = useState([]);
  const [mesos, setMesos] = useState([]);
  const [structOpen, setStructOpen] = useState(false);
  const [view, setView] = useState("week"); // week | season

  useEffect(() => {
    api.get("/groups").then((r) => { setGroups(r.data || []); if (!groupId && r.data?.length) setGroupId(r.data[0].id); }).catch(() => {});
    api.get("/competitions").then((r) => setComps(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { if (groupId) api.get("/mesocycles", { params: { group_id: groupId } }).then((r) => setMesos(r.data || [])).catch(() => {}); }, [groupId, structOpen]);

  const setPriority = async (cid, p) => {
    try { await api.put(`/competitions/${cid}/planning`, { technical_priority: p }); toast.success(`Priorità ${p}`); load(); } catch { toast.error("Errore"); }
  };

  const load = useCallback(() => {
    if (!groupId) return;
    setLoading(true);
    api.get("/programming/cockpit", { params: { group_id: groupId, week_start: weekStart } })
      .then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [groupId, weekStart]);
  useEffect(() => { load(); }, [load]);

  const shiftWeek = (n) => { const d = new Date(weekStart + "T00:00:00"); d.setDate(d.getDate() + n * 7); setWeekStart(iso(d)); };
  const goToday = () => setWeekStart(iso(mondayOf(new Date())));

  const mc = data?.microcycle;
  const prof = data?.profile;
  const weekEnd = data?.week_end;

  const openRegister = async (trainingId) => {
    try {
      const { data: reg } = await api.post("/attendance", { training_id: trainingId });
      setRegisterId(reg.id);
    } catch { toast.error("Impossibile aprire le presenze"); }
  };

  return (
    <div className={`space-y-6 ${view === "season" ? "" : "max-w-3xl"}`} data-testid="programming-page">
      <header className="space-y-3">
        <div className="flex items-center gap-2 text-[#0052FF]">
          <CalendarRange className="w-5 h-5" />
          <h1 className="font-heading text-3xl font-bold tracking-tight">Programmazione</h1>
        </div>
        <p className="text-sm text-zinc-500">Weekly Technical Cockpit · la potenza sta nel motore, non nell'interfaccia.</p>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center border border-zinc-200 rounded-md overflow-hidden">
            <button data-testid="view-week" onClick={() => setView("week")} className={`px-3 py-2 text-sm ${view === "week" ? "bg-black text-white" : "hover:bg-zinc-50"}`}>Settimana</button>
            <button data-testid="view-season" onClick={() => setView("season")} className={`px-3 py-2 text-sm ${view === "season" ? "bg-black text-white" : "hover:bg-zinc-50"}`}>Stagione</button>
          </div>
          <Select value={groupId} onValueChange={setGroupId}>
            <SelectTrigger data-testid="cockpit-group" className="h-10 w-56"><SelectValue placeholder="Gruppo" /></SelectTrigger>
            <SelectContent className="max-h-72">{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
          </Select>
          <div className="flex items-center border border-zinc-200 rounded-md overflow-hidden">
            <button data-testid="week-prev" onClick={() => shiftWeek(-1)} className="p-2 hover:bg-zinc-50"><ChevronLeft className="w-4 h-4" /></button>
            <button data-testid="week-today" onClick={goToday} className="px-3 py-2 text-sm border-x border-zinc-200 hover:bg-zinc-50">Oggi</button>
            <button data-testid="week-next" onClick={() => shiftWeek(1)} className="p-2 hover:bg-zinc-50"><ChevronRight className="w-4 h-4" /></button>
          </div>
          <div className="text-sm font-mono text-zinc-600" data-testid="week-range">{dayLabel(weekStart)} → {weekEnd ? dayLabel(weekEnd) : ""}</div>
        </div>
      </header>

      {loading && <div className="text-zinc-500 py-10 text-center">Caricamento…</div>}
      {!loading && !groupId && <div className="text-zinc-500 py-10 text-center">Seleziona un gruppo.</div>}

      {!loading && groupId && view === "season" && <SeasonTimeline groupId={groupId} currentWeek={weekStart} onSelectWeek={(ws) => { setWeekStart(ws); setView("week"); }} />}

      {!loading && data && view === "week" && (
        <>
          {/* Contesto: Stagione → Macro → Meso → Micro + prossima gara + timeline */}
          <section className="bg-gradient-to-r from-zinc-900 to-zinc-800 text-white rounded-md p-4 space-y-3" data-testid="cockpit-context">
            <div className="flex items-center gap-1.5 text-[11px] font-mono text-zinc-300 flex-wrap" data-testid="context-chain">
              <span>{data.season?.name || "Stagione"}</span>
              <span className="text-zinc-500">→</span>
              <span className={data.macrocycle ? "" : "text-zinc-500"}>{data.macrocycle?.name || "Macro —"}</span>
              <span className="text-zinc-500">→</span>
              <span className={data.mesocycle ? "text-emerald-300" : "text-zinc-500"}>{data.mesocycle?.name || "Meso —"}</span>
              <span className="text-zinc-500">→</span>
              <span className="text-white font-semibold">{mc?.primary_objective || "Settimana"}</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-400">Obiettivo mesociclo</div>
                <div className="text-sm">{data.mesocycle?.primary_objective || "—"}</div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-400 mt-2">Obiettivo settimana</div>
                <div className="text-sm">{mc?.primary_objective || "—"}</div>
              </div>
              {data.next_event ? (
                <div className="bg-white/10 rounded p-3" data-testid="next-event">
                  <div className="flex items-center gap-2">
                    <Trophy className="w-4 h-4 text-amber-300" />
                    <span className="font-semibold text-sm truncate">{data.next_event.name}</span>
                  </div>
                  <div className="text-xs text-zinc-300 font-mono mt-1">{dayLabel(data.next_event.date)}{data.next_event.location ? ` · ${data.next_event.location}` : ""}</div>
                  <div className="flex items-center gap-2 mt-2">
                    {data.next_event.days_remaining != null && <span className="text-xs bg-amber-400 text-black rounded px-2 py-0.5 font-bold" data-testid="event-countdown">{data.next_event.days_remaining >= 0 ? `${data.next_event.days_remaining} giorni` : "passata"}</span>}
                    <div className="flex items-center gap-1">
                      {["A", "B", "C"].map((p) => (
                        <button key={p} data-testid={`prio-${p}`} onClick={() => setPriority(data.next_event.id, p)}
                          className={`text-[11px] w-6 h-6 rounded-full font-bold ${data.next_event.technical_priority === p ? "bg-white text-black" : "bg-white/15 text-white hover:bg-white/25"}`}>{p}</button>
                      ))}
                    </div>
                    {data.next_event.planning_status && <span className="text-[10px] text-zinc-300">{data.next_event.planning_status}</span>}
                  </div>
                </div>
              ) : (
                <div className="bg-white/10 rounded p-3 text-xs text-zinc-300 flex items-center" data-testid="next-event-empty">Nessuna gara collegata al microciclo.</div>
              )}
            </div>
            {(data.timeline || []).length > 0 && (
              <div className="flex items-center gap-1.5 overflow-x-auto pb-1" data-testid="timeline">
                {data.timeline.map((t, i) => (
                  <div key={t.id} className="flex items-center gap-1.5 shrink-0">
                    <button data-testid={`timeline-mc-${i}`} onClick={() => setWeekStart(t.week_start)}
                      title={t.primary_objective || ""}
                      className={`text-[11px] px-2 py-1 rounded ${t.week_start === (data.week_start) ? "bg-emerald-400 text-black font-bold" : "bg-white/15 text-white hover:bg-white/25"}`}>
                      MC{i + 1}
                    </button>
                    {i < data.timeline.length - 1 && <span className="text-zinc-500">→</span>}
                  </div>
                ))}
                {data.next_event && <><span className="text-zinc-500">→</span><span className="text-[11px] px-2 py-1 rounded bg-amber-400 text-black font-bold shrink-0">GARA</span></>}
              </div>
            )}
            <div className="flex justify-end">
              <button data-testid="open-structure" onClick={() => setStructOpen(true)} className="text-[11px] uppercase tracking-wider text-zinc-300 hover:text-white flex items-center gap-1"><Layers className="w-3.5 h-3.5" /> Struttura stagione</button>
            </div>
          </section>

          {/* Microciclo */}
          <section className="bg-white border border-zinc-200 rounded-md p-5" data-testid="cockpit-microcycle">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading text-lg font-semibold flex items-center gap-2"><Target className="w-4 h-4 text-zinc-400" /> Microciclo</h2>
              <button data-testid="edit-microcycle" onClick={() => setMcOpen(true)} className="text-xs uppercase tracking-wider text-[#0052FF] flex items-center gap-1"><Settings2 className="w-3.5 h-3.5" /> {mc ? "Modifica" : "Crea"}</button>
            </div>
            {!mc ? (
              <div className="text-sm text-zinc-500" data-testid="microcycle-empty">Nessun microciclo per questa settimana. Creane uno per definire obiettivo, carico e specificità.</div>
            ) : (
              <div className="space-y-3">
                <div className="text-base font-medium">{mc.primary_objective || "—"}</div>
                <div className="flex flex-wrap gap-2">
                  <span className={`text-xs px-2.5 py-1 rounded-full border ${loadTone[mc.planned_load] || ""}`}><Gauge className="w-3 h-3 inline mr-1" />Carico: {mc.planned_load}</span>
                  <span className={`text-xs px-2.5 py-1 rounded-full border ${specTone[mc.specificity] || ""}`}><Crosshair className="w-3 h-3 inline mr-1" />Specificità: {mc.specificity}</span>
                  <span className="text-xs px-2.5 py-1 rounded-full border bg-zinc-50 text-zinc-600 border-zinc-200">{mc.status}</span>
                </div>
                {(mc.focus_tags || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5">{mc.focus_tags.map((f) => <span key={f} className="text-[11px] px-2 py-0.5 rounded bg-zinc-100 text-zinc-700">#{f}</span>)}</div>
                )}
                {mc.technical_notes && <p className="text-sm text-zinc-600">{mc.technical_notes}</p>}
              </div>
            )}
          </section>

          {/* Profilo tecnico gruppo */}
          <section className="bg-white border border-zinc-200 rounded-md p-5" data-testid="cockpit-profile">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading text-lg font-semibold">Profilo tecnico</h2>
              <button data-testid="edit-profile" onClick={() => setProfOpen(true)} className="text-xs uppercase tracking-wider text-[#0052FF]">{prof ? "Modifica" : "Definisci"}</button>
            </div>
            {!prof ? <div className="text-sm text-zinc-500" data-testid="profile-empty">Profilo tecnico non definito per questo gruppo.</div> : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-2 gap-x-4 text-sm">
                <Info label="Arma" v={prof.weapon} /><Info label="Livello" v={prof.level} /><Info label="Orientamento" v={prof.orientation} />
                <Info label="Giorni" v={(prof.days || []).join(", ")} /><Info label="Orario" v={prof.time_start && prof.time_end ? `${prof.time_start}–${prof.time_end}` : prof.time_start} /><Info label="Durata" v={prof.standard_duration ? `${prof.standard_duration}′` : "—"} />
                {prof.recurring_notes && <div className="col-span-full text-zinc-600">{prof.recurring_notes}</div>}
              </div>
            )}
          </section>

          {/* Sessioni della settimana */}
          <section data-testid="cockpit-sessions">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading text-lg font-semibold">Sessioni della settimana</h2>
              <button data-testid="create-session-btn" disabled={!mc} onClick={() => setSessOpen(true)}
                className={`text-xs uppercase tracking-wider flex items-center gap-1 px-2.5 py-1.5 rounded-md ${mc ? "bg-[#0052FF] text-white hover:bg-blue-700" : "bg-zinc-100 text-zinc-400 cursor-not-allowed"}`}>
                <Plus className="w-3.5 h-3.5" /> Crea sessione
              </button>
            </div>
            {data.week_summary && data.week_summary.sessions_total > 0 && (
              <div className="flex items-center gap-4 text-sm mb-3 px-1" data-testid="week-summary">
                <span className="font-mono"><span className="text-zinc-400 text-xs uppercase tracking-wider mr-1">Sessioni</span><span className="font-bold">{data.week_summary.sessions_done}</span>/<span>{data.week_summary.sessions_total}</span></span>
                <span className="font-mono"><span className="text-zinc-400 text-xs uppercase tracking-wider mr-1">Minuti</span><span className="font-bold text-[#0052FF]">{data.week_summary.effective_minutes}′</span>/<span>{data.week_summary.planned_minutes}′</span></span>
                {(data.week_summary.by_category || []).some((r) => r.realized > 0) && (
                  <div className="flex items-center gap-2 overflow-x-auto no-scrollbar" data-testid="week-summary-cat">
                    {data.week_summary.by_category.filter((r) => r.planned > 0 || r.realized > 0).slice(0, 6).map((r) => (
                      <span key={r.category} className="text-[11px] whitespace-nowrap text-zinc-500">{r.category} <span className="font-mono">{r.planned}→<span className="text-[#0052FF]">{r.realized}</span></span></span>
                    ))}
                  </div>
                )}
              </div>
            )}
            {(data.sessions || []).length === 0 ? (
              <div className="text-sm text-zinc-500 py-6 text-center border border-dashed border-zinc-200 rounded-md" data-testid="sessions-empty">
                Nessuna sessione questa settimana{mc ? " · usa 'Crea sessione' dal microciclo" : ""}.
              </div>
            ) : (
              <div className="space-y-2">
                {data.sessions.map((s) => (
                  <div key={s.id} data-testid={`session-${s.id}`} className="bg-white border border-zinc-200 rounded-md p-3">
                    <div className="flex items-start gap-3">
                      <div className="text-sm font-mono font-semibold w-24 shrink-0">
                        <div>{dayLabel(s.date)}</div>
                        <div className="text-zinc-500">{extractTime(s.date) || "—"}</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium truncate">{s.objective || s.title || "Allenamento"}</span>
                          <StatusPill status={s.status} />
                        </div>
                        <div className="text-xs text-zinc-500 font-mono mt-0.5">
                          {s.duration || 90}′
                          {s.status === "completato" && s.present != null && ` · ${s.present} presenti`}
                          {s.status === "completato" && s.effective_minutes != null && ` · ${s.effective_minutes}′ effettivi`}
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <Link to={`/allenamento/${s.id}`} data-testid={`session-open-${s.id}`} className="text-xs uppercase tracking-wider border border-zinc-200 rounded px-2 py-1 hover:bg-zinc-50">Apri</Link>
                          <button onClick={() => openRegister(s.id)} data-testid={`session-att-${s.id}`} className="text-xs uppercase tracking-wider bg-black text-white rounded px-2 py-1 hover:bg-zinc-800">Presenze</button>
                          <Link to={`/allenamento/${s.id}`} data-testid={`session-edit-${s.id}`} className="text-xs uppercase tracking-wider text-zinc-500 hover:text-black">Modifica</Link>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}

      {mcOpen && <MicrocycleDialog groupId={groupId} weekStart={weekStart} weekEnd={weekEnd || iso(new Date(new Date(weekStart).getTime() + 6 * 864e5))} mc={mc} mesos={mesos} comps={comps} onClose={() => setMcOpen(false)} onSaved={() => { setMcOpen(false); load(); }} />}
      {structOpen && <StructureDialog groupId={groupId} comps={comps} onClose={() => setStructOpen(false)} onSaved={load} />}
      {profOpen && <ProfileDialog groupId={groupId} prof={prof} onClose={() => setProfOpen(false)} onSaved={() => { setProfOpen(false); load(); }} />}
      {sessOpen && mc && <CreateSessionDialog mc={mc} prof={prof} weekStart={weekStart} onClose={() => setSessOpen(false)} onSaved={() => { setSessOpen(false); load(); }} />}
      {registerId && <AttendanceRegisterEditor registerId={registerId} siblings={[]} onNavigate={() => {}} onClose={() => { setRegisterId(null); load(); }} onSaved={load} />}
    </div>
  );
}

function Info({ label, v }) { return <div><div className="text-[10px] uppercase tracking-wider text-zinc-400">{label}</div><div>{v || "—"}</div></div>; }
function StatusPill({ status }) {
  const map = { completato: "bg-emerald-50 text-emerald-700", registro: "bg-amber-50 text-amber-700", pianificato: "bg-zinc-100 text-zinc-600" };
  return <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${map[status] || map.pianificato}`}>{status}</span>;
}

function MicrocycleDialog({ groupId, weekStart, weekEnd, mc, mesos = [], comps = [], onClose, onSaved }) {
  const [f, setF] = useState({
    primary_objective: mc?.primary_objective || "", planned_load: mc?.planned_load || "Medio",
    specificity: mc?.specificity || "Media", focus_tags: (mc?.focus_tags || []).join(", "),
    technical_notes: mc?.technical_notes || "", status: mc?.status || "bozza",
    mesocycle_id: mc?.mesocycle_id || null, reference_event_ids: mc?.reference_event_ids || [],
  });
  const toggleEvent = (id) => setF((p) => ({ ...p, reference_event_ids: p.reference_event_ids.includes(id) ? p.reference_event_ids.filter((x) => x !== id) : [...p.reference_event_ids, id] }));
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const body = { group_id: groupId, week_start: weekStart, week_end: weekEnd, primary_objective: f.primary_objective, planned_load: f.planned_load, specificity: f.specificity, focus_tags: f.focus_tags.split(",").map((x) => x.trim()).filter(Boolean), technical_notes: f.technical_notes, status: f.status, mesocycle_id: f.mesocycle_id, reference_event_ids: f.reference_event_ids, macrocycle_id: (mesos.find((m) => m.id === f.mesocycle_id) || {}).macrocycle_id || null };
      if (mc?.id) await api.put(`/microcycles/${mc.id}`, body); else await api.post("/microcycles", body);
      toast.success("Microciclo salvato"); onSaved();
    } catch { toast.error("Errore salvataggio"); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="microcycle-dialog">
        <DialogHeader><DialogTitle>Microciclo · settimana</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Obiettivo primario"><Input data-testid="mc-objective" value={f.primary_objective} onChange={(e) => setF({ ...f, primary_objective: e.target.value })} placeholder="Es. costruzione/transizione specifica" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Carico"><Sel v={f.planned_load} on={(v) => setF({ ...f, planned_load: v })} opts={LOADS} testid="mc-load" /></Field>
            <Field label="Specificità"><Sel v={f.specificity} on={(v) => setF({ ...f, specificity: v })} opts={SPECS} testid="mc-spec" /></Field>
          </div>
          <Field label="Focus (separati da virgola)"><Input data-testid="mc-focus" value={f.focus_tags} onChange={(e) => setF({ ...f, focus_tags: e.target.value })} placeholder="assalti, footwork, distanza" /></Field>
          <Field label="Note tecniche"><Textarea data-testid="mc-notes" value={f.technical_notes} onChange={(e) => setF({ ...f, technical_notes: e.target.value })} rows={3} /></Field>
          <Field label="Stato"><Sel v={f.status} on={(v) => setF({ ...f, status: v })} opts={["bozza", "attivo", "completato"]} testid="mc-status" /></Field>
          <Field label="Mesociclo (opzionale)">
            <Sel v={f.mesocycle_id || "none"} on={(v) => setF({ ...f, mesocycle_id: v === "none" ? null : v })} testid="mc-meso" opts={["none", ...mesos.map((m) => m.id)]} labels={{ none: "— nessuno —", ...Object.fromEntries(mesos.map((m) => [m.id, m.name])) }} />
          </Field>
          <Field label="Gare collegate (opzionale)">
            <div className="flex flex-wrap gap-1.5" data-testid="mc-events">
              {comps.length === 0 && <span className="text-xs text-zinc-400">Nessuna gara disponibile</span>}
              {comps.slice(0, 12).map((e) => (
                <button key={e.id} type="button" data-testid={`mc-event-${e.id}`} onClick={() => toggleEvent(e.id)}
                  className={`text-[11px] px-2 py-1 rounded-full border ${f.reference_event_ids.includes(e.id) ? "bg-black text-white border-black" : "bg-white text-zinc-600 border-zinc-300"}`}>
                  {e.name} · {(e.date || "").slice(5, 10)}
                </button>
              ))}
            </div>
          </Field>
        </div>
        <DialogFooter><button onClick={onClose} className="px-4 py-2 text-sm border border-zinc-200 rounded-md">Annulla</button><button data-testid="mc-save" disabled={saving} onClick={save} className="px-4 py-2 text-sm bg-[#0052FF] text-white rounded-md">{saving ? "…" : "Salva"}</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProfileDialog({ groupId, prof, onClose, onSaved }) {
  const [f, setF] = useState({
    weapon: prof?.weapon || "", level: prof?.level || "", orientation: prof?.orientation || "",
    days: prof?.days || [], time_start: prof?.time_start || "", time_end: prof?.time_end || "",
    standard_duration: prof?.standard_duration || 90, recurring_notes: prof?.recurring_notes || "",
  });
  const [saving, setSaving] = useState(false);
  const toggleDay = (d) => setF((p) => ({ ...p, days: p.days.includes(d) ? p.days.filter((x) => x !== d) : [...p.days, d] }));
  const save = async () => {
    setSaving(true);
    try {
      const body = { group_id: groupId, ...f, standard_duration: parseInt(f.standard_duration) || 90 };
      if (prof?.id) await api.put(`/group-technical-profiles/${prof.id}`, body); else await api.post("/group-technical-profiles", body);
      toast.success("Profilo salvato"); onSaved();
    } catch { toast.error("Errore salvataggio"); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="profile-dialog">
        <DialogHeader><DialogTitle>Profilo tecnico gruppo</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <Field label="Arma"><Input data-testid="prof-weapon" value={f.weapon} onChange={(e) => setF({ ...f, weapon: e.target.value })} /></Field>
            <Field label="Livello"><Input data-testid="prof-level" value={f.level} onChange={(e) => setF({ ...f, level: e.target.value })} /></Field>
            <Field label="Orientamento"><Input data-testid="prof-orientation" value={f.orientation} onChange={(e) => setF({ ...f, orientation: e.target.value })} placeholder="agonistico" /></Field>
          </div>
          <Field label="Giorni">
            <div className="flex flex-wrap gap-1.5">{DAYS.map((d) => <button key={d} type="button" data-testid={`prof-day-${d}`} onClick={() => toggleDay(d)} className={`text-xs px-2.5 py-1 rounded-full border ${f.days.includes(d) ? "bg-black text-white border-black" : "bg-white text-zinc-600 border-zinc-300"}`}>{d}</button>)}</div>
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Inizio"><Input data-testid="prof-start" value={f.time_start} onChange={(e) => setF({ ...f, time_start: e.target.value })} placeholder="18:45" /></Field>
            <Field label="Fine"><Input data-testid="prof-end" value={f.time_end} onChange={(e) => setF({ ...f, time_end: e.target.value })} placeholder="20:30" /></Field>
            <Field label="Durata (min)"><Input data-testid="prof-duration" type="number" value={f.standard_duration} onChange={(e) => setF({ ...f, standard_duration: e.target.value })} /></Field>
          </div>
          <Field label="Note / regole ricorrenti"><Textarea data-testid="prof-notes" value={f.recurring_notes} onChange={(e) => setF({ ...f, recurring_notes: e.target.value })} rows={2} placeholder="venerdì prevalentemente assalti" /></Field>
        </div>
        <DialogFooter><button onClick={onClose} className="px-4 py-2 text-sm border border-zinc-200 rounded-md">Annulla</button><button data-testid="prof-save" disabled={saving} onClick={save} className="px-4 py-2 text-sm bg-[#0052FF] text-white rounded-md">{saving ? "…" : "Salva"}</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateSessionDialog({ mc, prof, weekStart, onClose, onSaved }) {
  const today = iso(new Date());
  const weekEndStr = iso(new Date(new Date(weekStart + "T00:00:00").getTime() + 6 * 864e5));
  const defaultDate = today >= weekStart && today <= weekEndStr ? today : weekStart;
  const [f, setF] = useState({ date: defaultDate, time: prof?.time_start || "18:45", duration: prof?.standard_duration || 90, objective: mc?.primary_objective || "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/microcycles/${mc.id}/create-session`, { date: f.date, time: f.time, duration: parseInt(f.duration) || undefined, objective: f.objective });
      if (data.created) toast.success("Sessione creata"); else toast.info("Sessione già esistente per gruppo/data/orario");
      onSaved();
    } catch { toast.error("Errore creazione"); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="create-session-dialog">
        <DialogHeader><DialogTitle>Crea sessione da microciclo</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Data"><Input data-testid="cs-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
            <Field label="Orario"><Input data-testid="cs-time" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} placeholder="18:45" /></Field>
          </div>
          <Field label="Durata (min)"><Input data-testid="cs-duration" type="number" value={f.duration} onChange={(e) => setF({ ...f, duration: e.target.value })} /></Field>
          <Field label="Obiettivo"><Input data-testid="cs-objective" value={f.objective} onChange={(e) => setF({ ...f, objective: e.target.value })} /></Field>
        </div>
        <DialogFooter><button onClick={onClose} className="px-4 py-2 text-sm border border-zinc-200 rounded-md">Annulla</button><button data-testid="cs-save" disabled={saving} onClick={save} className="px-4 py-2 text-sm bg-[#0052FF] text-white rounded-md">{saving ? "…" : "Crea"}</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StructureDialog({ groupId, comps, onClose, onSaved }) {
  const [macros, setMacros] = useState([]);
  const [mesos, setMesos] = useState([]);
  const [nm, setNm] = useState({ name: "", start_date: "", end_date: "", objective: "" });
  const [ns, setNs] = useState({ name: "", start_date: "", end_date: "", primary_objective: "", macrocycle_id: null, reference_event_ids: [] });
  const reload = () => {
    api.get("/macrocycles", { params: { group_id: groupId } }).then((r) => setMacros(r.data || [])).catch(() => {});
    api.get("/mesocycles", { params: { group_id: groupId } }).then((r) => setMesos(r.data || [])).catch(() => {});
  };
  useEffect(reload, [groupId]);
  const addMacro = async () => { if (!nm.name) return; await api.post("/macrocycles", { ...nm, group_ids: [groupId] }); setNm({ name: "", start_date: "", end_date: "", objective: "" }); reload(); onSaved(); toast.success("Macrociclo creato"); };
  const addMeso = async () => { if (!ns.name) return; await api.post("/mesocycles", { ...ns, group_ids: [groupId] }); setNs({ name: "", start_date: "", end_date: "", primary_objective: "", macrocycle_id: null, reference_event_ids: [] }); reload(); onSaved(); toast.success("Mesociclo creato"); };
  const delMacro = async (id) => { await api.delete(`/macrocycles/${id}`); reload(); onSaved(); };
  const delMeso = async (id) => { await api.delete(`/mesocycles/${id}`); reload(); onSaved(); };
  const toggleEv = (id) => setNs((p) => ({ ...p, reference_event_ids: p.reference_event_ids.includes(id) ? p.reference_event_ids.filter((x) => x !== id) : [...p.reference_event_ids, id] }));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="structure-dialog">
        <DialogHeader><DialogTitle>Struttura stagione</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <div>
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Macrocicli</div>
            {macros.map((m) => <div key={m.id} className="flex items-center justify-between text-sm py-1"><span>{m.name}</span><button data-testid={`del-macro-${m.id}`} onClick={() => delMacro(m.id)} className="text-red-600 text-xs">Elimina</button></div>)}
            <div className="grid grid-cols-2 gap-2 mt-2">
              <Input data-testid="macro-name" placeholder="Nome macrociclo" value={nm.name} onChange={(e) => setNm({ ...nm, name: e.target.value })} />
              <Input data-testid="macro-obj" placeholder="Obiettivo" value={nm.objective} onChange={(e) => setNm({ ...nm, objective: e.target.value })} />
              <Input type="date" value={nm.start_date} onChange={(e) => setNm({ ...nm, start_date: e.target.value })} />
              <Input type="date" value={nm.end_date} onChange={(e) => setNm({ ...nm, end_date: e.target.value })} />
            </div>
            <button data-testid="add-macro" onClick={addMacro} className="mt-2 text-xs bg-zinc-900 text-white rounded px-3 py-1.5">+ Macrociclo</button>
          </div>
          <div className="border-t border-zinc-100 pt-4">
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Mesocicli</div>
            {mesos.map((m) => <div key={m.id} className="flex items-center justify-between text-sm py-1"><span>{m.name}</span><button data-testid={`del-meso-${m.id}`} onClick={() => delMeso(m.id)} className="text-red-600 text-xs">Elimina</button></div>)}
            <div className="grid grid-cols-2 gap-2 mt-2">
              <Input data-testid="meso-name" placeholder="Nome mesociclo" value={ns.name} onChange={(e) => setNs({ ...ns, name: e.target.value })} />
              <Input data-testid="meso-obj" placeholder="Obiettivo primario" value={ns.primary_objective} onChange={(e) => setNs({ ...ns, primary_objective: e.target.value })} />
              <Input type="date" value={ns.start_date} onChange={(e) => setNs({ ...ns, start_date: e.target.value })} />
              <Input type="date" value={ns.end_date} onChange={(e) => setNs({ ...ns, end_date: e.target.value })} />
            </div>
            <div className="mt-2"><Sel v={ns.macrocycle_id || "none"} on={(v) => setNs({ ...ns, macrocycle_id: v === "none" ? null : v })} testid="meso-macro" opts={["none", ...macros.map((m) => m.id)]} labels={{ none: "— senza macro —", ...Object.fromEntries(macros.map((m) => [m.id, m.name])) }} /></div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {comps.slice(0, 10).map((e) => <button key={e.id} type="button" data-testid={`meso-event-${e.id}`} onClick={() => toggleEv(e.id)} className={`text-[11px] px-2 py-1 rounded-full border ${ns.reference_event_ids.includes(e.id) ? "bg-black text-white border-black" : "bg-white text-zinc-600 border-zinc-300"}`}>{e.name}</button>)}
            </div>
            <button data-testid="add-meso" onClick={addMeso} className="mt-2 text-xs bg-zinc-900 text-white rounded px-3 py-1.5">+ Mesociclo</button>
          </div>
        </div>
        <DialogFooter><button onClick={onClose} className="px-4 py-2 text-sm border border-zinc-200 rounded-md">Chiudi</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) { return <div><div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>{children}</div>; }
function Sel({ v, on, opts, testid, labels }) {
  return <Select value={v} onValueChange={on}><SelectTrigger data-testid={testid} className="h-10"><SelectValue /></SelectTrigger><SelectContent>{opts.map((o) => <SelectItem key={o} value={o}>{labels?.[o] ?? o}</SelectItem>)}</SelectContent></Select>;
}
